import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router";
import { Popover } from "@base-ui/react/popover";
import { Button, toast } from "@/ui";
import { notifications as notificationsApi } from "../lib/api";
import { NOTIFICATIONS_QUERY_KEY } from "../lib/query-keys";
import { useWebPush } from "../hooks/useWebPush";
import { patchNotificationState } from "../lib/notifications-cache";

interface Props {
    native: boolean;
    nativeState: string;
    requestNative: () => Promise<void> | void;
}

function relativeRoute(value: string): string {
    try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin) return value;
        const pathname = url.pathname.replace(/^\/mail\/inbox(?=\/|$)/, "/inbox");
        return `${pathname}${url.search}${url.hash}`;
    } catch {
        return value;
    }
}

export default function NotificationCenter({ native, nativeState, requestNative }: Props) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const webPush = useWebPush(!native);
    const [enablingPush, setEnablingPush] = useState(false);
    const [pushError, setPushError] = useState<string | null>(null);
    const preferences = useQuery({
        queryKey: ["notification-preferences"],
        queryFn: notificationsApi.preferences,
        enabled: !native && webPush.supported,
    });
    const browserPushOn = webPush.state === "subscribed"
        && preferences.data?.preferences.webPushEnabled
        && preferences.data?.preferences.mailEnabled;
    const inbox = useQuery({
        queryKey: NOTIFICATIONS_QUERY_KEY,
        queryFn: notificationsApi.list,
        refetchInterval: 60_000,
        staleTime: 15_000,
    });
    // Optimistic: read/dismiss shows immediately; rolled back if the server refuses.
    const mutate = useMutation({
        mutationFn: notificationsApi.updateState,
        onMutate: async (vars) => {
            await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
            const previous = queryClient.getQueryData(NOTIFICATIONS_QUERY_KEY);
            patchNotificationState(queryClient, {
                ids: vars.all ? null : vars.ids ?? [],
                dismissed: Boolean(vars.dismissed),
                at: new Date().toISOString(),
            });
            return { previous };
        },
        onError: (_error, _vars, context) => {
            if (context) queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previous);
            toast.error("Could not update notifications.");
        },
    });
    const items = inbox.data?.notifications ?? [];
    const unread = inbox.data?.unread ?? 0;

    async function enablePush() {
        if (enablingPush) return;
        setEnablingPush(true);
        setPushError(null);
        try {
            if (native) {
                await requestNative();
                return;
            }
            if (!await webPush.subscribe()) return;
            const result = await notificationsApi.updatePreferences({ webPushEnabled: true, mailEnabled: true });
            queryClient.setQueryData(["notification-preferences"], result);
            toast.success("Browser alerts for new emails enabled");
        } catch {
            setPushError("Could not enable alerts. Please try again.");
        } finally {
            setEnablingPush(false);
        }
    }

    return (
        <Popover.Root>
            <Popover.Trigger render={<Button variant="ghost" size="sm" className="icon-button notification-trigger" aria-label={unread ? `${unread} unread notifications` : "Notifications"} />}>
                <Bell />{unread > 0 && <span className="notification-count">{unread > 99 ? "99+" : unread}</span>}
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Positioner sideOffset={8} align="end" collisionPadding={8} className="notification-positioner">
                    <Popover.Popup className="notification-popover">
                        <div className="notification-header">
                            <div><strong>Notifications</strong><span>{unread ? `${unread} unread` : "All caught up"}</span></div>
                            {unread > 0 && <Button variant="ghost" size="sm" onClick={() => mutate.mutate({ all: true })}>Mark all read</Button>}
                        </div>
                        <div className="notification-list">
                            {inbox.isLoading && <p className="notification-empty">Loading…</p>}
                            {!inbox.isLoading && items.length === 0 && <p className="notification-empty">You’re all caught up.</p>}
                            {items.map(item => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`notification-item${item.readAt ? "" : " unread"}`}
                                    onClick={() => {
                                        if (!item.readAt) mutate.mutate({ ids: [item.id] });
                                        if (item.url) {
                                            const route = relativeRoute(item.url);
                                            if (/^https?:\/\//.test(route)) window.location.assign(route);
                                            else navigate(route);
                                        }
                                    }}
                                >
                                    <span className={`notification-category ${item.category}`}>{item.category}</span>
                                    <strong>{item.title}</strong>
                                    {item.body && <span>{item.body}</span>}
                                    <time>{new Date(item.occurredAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</time>
                                </button>
                            ))}
                        </div>
                        {((native && nativeState !== "granted") || (!native && webPush.supported)) && (
                            <div className="notification-footer notification-push-footer">
                                <div aria-live="polite">
                                    <strong>{browserPushOn ? "Browser push is on" : "Get new email alerts"}</strong>
                                    <p>{webPush.state === "denied"
                                        ? "Allow notifications for this site in your browser’s site settings, then reload."
                                        : pushError ?? webPush.error ?? (browserPushOn
                                            ? "This browser will notify you when new email arrives."
                                            : "Receive notifications even when Kardoe is in the background.")}</p>
                                </div>
                                <Button variant="secondary" size="sm"
                                    disabled={enablingPush || (!native && (webPush.state === "working" || webPush.state === "denied" || Boolean(browserPushOn)))}
                                    onClick={() => void enablePush()}>
                                    {enablingPush || (!native && webPush.state === "working") ? "Turning on…"
                                        : native ? "Turn on notifications"
                                        : webPush.state === "denied" ? "Blocked by browser"
                                        : browserPushOn ? "Browser push on" : "Turn on browser push"}
                                </Button>
                            </div>
                        )}
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    );
}
